import { getAllRoutes } from '../constants/routes';

/**
 * Enhanced route validation and cleanup utility
 */
export class RouteValidatorManager {
  constructor() {
    this.validRoutes = new Set();
    this.deadRoutes = new Set();
    this.redirectRoutes = new Map();
    this.environmentRoutes = new Map();
    
    this.environments = {
      local: 'http://localhost:5173',
      staging: process.env.VITE_STAGING_URL || 'https://staging.bootminds.com',
      production: process.env.VITE_PRODUCTION_URL || 'https://bootminds.com'
    };
  }

  /**
   * Initialize route validation
   */
  async initialize() {
    console.log('🔍 Initializing route validation...');
    
    // Get all configured routes
    const allRoutes = getAllRoutes();
    
    allRoutes.forEach(route => {
      if (route.redirect) {
        this.redirectRoutes.set(route.path, route.redirect);
      } else {
        this.validRoutes.add(route.path);
      }
    });

    console.log(`📋 Found ${this.validRoutes.size} valid routes and ${this.redirectRoutes.size} redirects`);
  }

  /**
   * Validate all routes across environments
   */
  async validateAllRoutes() {
    await this.initialize();
    
    const results = {
      timestamp: new Date().toISOString(),
      environments: {},
      summary: {
        totalRoutes: this.validRoutes.size,
        validAcrossAll: 0,
        partiallyValid: 0,
        completelyInvalid: 0,
        redirectsWorking: 0,
        deadRoutes: []
      }
    };

    // Test each environment
    for (const [envName, baseUrl] of Object.entries(this.environments)) {
      console.log(`🌍 Testing ${envName} environment: ${baseUrl}`);
      
      results.environments[envName] = await this.validateEnvironment(baseUrl);
    }

    // Analyze cross-environment results
    this.analyzeCrossEnvironmentResults(results);
    
    return results;
  }

  /**
   * Validate routes in a specific environment
   */
  async validateEnvironment(baseUrl) {
    const results = {
      baseUrl,
      routes: [],
      redirects: [],
      summary: {
        total: 0,
        working: 0,
        broken: 0,
        redirectsWorking: 0,
        redirectsBroken: 0
      }
    };

    // Test regular routes
    for (const route of this.validRoutes) {
      const url = `${baseUrl}${route}`;
      const validation = await this.validateSingleRoute(url);
      
      results.routes.push({
        path: route,
        url,
        ...validation
      });

      results.summary.total++;
      if (validation.isValid) {
        results.summary.working++;
      } else {
        results.summary.broken++;
      }
    }

    // Test redirect routes
    for (const [from, to] of this.redirectRoutes) {
      const fromUrl = `${baseUrl}${from}`;
      const expectedToUrl = `${baseUrl}${to}`;
      
      const validation = await this.validateRedirect(fromUrl, expectedToUrl);
      
      results.redirects.push({
        from,
        to,
        fromUrl,
        expectedToUrl,
        ...validation
      });

      if (validation.isValid) {
        results.summary.redirectsWorking++;
      } else {
        results.summary.redirectsBroken++;
      }
    }

    return results;
  }

  /**
   * Validate a single route
   */
  async validateSingleRoute(url) {
    try {
      const startTime = Date.now();
      
      const response = await fetch(url, {
        method: 'HEAD',
        timeout: 10000,
        headers: {
          'User-Agent': 'Route-Validator/1.0'
        }
      });

      const endTime = Date.now();
      const responseTime = endTime - startTime;

      return {
        isValid: response.ok,
        statusCode: response.status,
        statusText: response.statusText,
        responseTime,
        contentType: response.headers.get('content-type') || '',
        cacheControl: response.headers.get('cache-control') || '',
        error: null
      };
    } catch (error) {
      return {
        isValid: false,
        statusCode: null,
        statusText: null,
        responseTime: null,
        contentType: '',
        cacheControl: '',
        error: error.message
      };
    }
  }

  /**
   * Validate a redirect
   */
  async validateRedirect(fromUrl, expectedToUrl) {
    try {
      const response = await fetch(fromUrl, {
        method: 'HEAD',
        redirect: 'manual',
        timeout: 10000,
        headers: {
          'User-Agent': 'Route-Validator/1.0'
        }
      });

      const actualLocation = response.headers.get('location');
      const isRedirect = [301, 302, 303, 307, 308].includes(response.status);
      
      if (!isRedirect) {
        return {
          isValid: false,
          statusCode: response.status,
          error: 'Expected redirect but got different status',
          actualLocation: null,
          redirectType: null
        };
      }

      // Normalize URLs for comparison
      const normalizedActual = this.normalizeUrl(actualLocation, fromUrl);
      const normalizedExpected = this.normalizeUrl(expectedToUrl);
      
      return {
        isValid: normalizedActual === normalizedExpected,
        statusCode: response.status,
        redirectType: this.getRedirectType(response.status),
        actualLocation: normalizedActual,
        expectedLocation: normalizedExpected,
        error: normalizedActual !== normalizedExpected 
          ? `Redirect mismatch: expected ${normalizedExpected}, got ${normalizedActual}`
          : null
      };
    } catch (error) {
      return {
        isValid: false,
        statusCode: null,
        redirectType: null,
        actualLocation: null,
        expectedLocation: expectedToUrl,
        error: error.message
      };
    }
  }

  /**
   * Normalize URL for comparison
   */
  normalizeUrl(url, base = null) {
    if (!url) return null;
    
    try {
      const normalizedUrl = new URL(url, base);
      // Remove trailing slash and normalize
      return normalizedUrl.href.replace(/\/$/, '');
    } catch (error) {
      return url;
    }
  }

  /**
   * Get redirect type description
   */
  getRedirectType(statusCode) {
    const types = {
      301: 'Permanent Redirect',
      302: 'Temporary Redirect',
      303: 'See Other',
      307: 'Temporary Redirect (Method Preserved)',
      308: 'Permanent Redirect (Method Preserved)'
    };
    return types[statusCode] || 'Unknown Redirect';
  }

  /**
   * Analyze cross-environment results
   */
  analyzeCrossEnvironmentResults(results) {
    const routeHealth = new Map();
    
    // Initialize route health tracking
    for (const route of this.validRoutes) {
      routeHealth.set(route, {
        path: route,
        workingEnvironments: [],
        brokenEnvironments: [],
        totalEnvironments: Object.keys(this.environments).length
      });
    }

    // Analyze each environment's results
    Object.entries(results.environments).forEach(([envName, envResults]) => {
      envResults.routes.forEach(routeResult => {
        const health = routeHealth.get(routeResult.path);
        if (health) {
          if (routeResult.isValid) {
            health.workingEnvironments.push(envName);
          } else {
            health.brokenEnvironments.push({
              environment: envName,
              error: routeResult.error,
              statusCode: routeResult.statusCode
            });
          }
        }
      });
    });

    // Categorize routes
    const routeHealthArray = Array.from(routeHealth.values());
    
    results.summary.validAcrossAll = routeHealthArray.filter(
      route => route.workingEnvironments.length === route.totalEnvironments
    ).length;
    
    results.summary.partiallyValid = routeHealthArray.filter(
      route => route.workingEnvironments.length > 0 && route.workingEnvironments.length < route.totalEnvironments
    ).length;
    
    results.summary.completelyInvalid = routeHealthArray.filter(
      route => route.workingEnvironments.length === 0
    ).length;

    // Identify dead routes
    results.summary.deadRoutes = routeHealthArray
      .filter(route => route.workingEnvironments.length === 0)
      .map(route => ({
        path: route.path,
        issues: route.brokenEnvironments
      }));

    // Count working redirects
    results.summary.redirectsWorking = Object.values(results.environments)
      .reduce((sum, env) => sum + env.summary.redirectsWorking, 0);

    // Add detailed route health
    results.routeHealth = routeHealthArray;
  }

  /**
   * Generate cleanup recommendations
   */
  generateCleanupRecommendations(validationResults) {
    const recommendations = {
      timestamp: new Date().toISOString(),
      deadRoutes: [],
      brokenRedirects: [],
      performanceIssues: [],
      securityIssues: [],
      seoIssues: []
    };

    // Identify completely dead routes
    recommendations.deadRoutes = validationResults.summary.deadRoutes.map(route => ({
      path: route.path,
      recommendation: 'Remove from route configuration and component imports',impact: 'High - Route is not accessible in any environment',
      action: `Delete route definition and component: ${route.path}`
    }));

    // Identify broken redirects
    Object.values(validationResults.environments).forEach(env => {
      env.redirects.forEach(redirect => {
        if (!redirect.isValid) {
          recommendations.brokenRedirects.push({
            from: redirect.from,
            to: redirect.to,
            environment: env.baseUrl,
            error: redirect.error,
            recommendation: 'Fix redirect configuration',
            action: `Update redirect from ${redirect.from} to ${redirect.to}`
          });
        }
      });
    });

    // Identify performance issues
    Object.values(validationResults.environments).forEach(env => {
      env.routes.forEach(route => {
        if (route.responseTime && route.responseTime > 3000) {
          recommendations.performanceIssues.push({
            path: route.path,
            environment: env.baseUrl,
            responseTime: route.responseTime,
            recommendation: 'Optimize route performance',
            action: `Investigate slow loading for ${route.path} (${route.responseTime}ms)`
          });
        }
      });
    });

    // Identify security issues
    Object.values(validationResults.environments).forEach(env => {
      env.routes.forEach(route => {
        if (route.isValid && !env.baseUrl.startsWith('https://')) {
          recommendations.securityIssues.push({
            path: route.path,
            environment: env.baseUrl,
            recommendation: 'Use HTTPS for production routes',
            action: `Ensure ${route.path} uses HTTPS in production`
          });
        }
      });
    });

    return recommendations;
  }

  /**
   * Generate 404 fallback configuration
   */
  generate404Config() {
    return {
      // Nginx configuration
      nginx: `
# 404 handling for React Router
location / {
  try_files $uri $uri/ /index.html;
}

# Custom 404 for API routes
location /api/ {
  try_files $uri $uri/ @api_404;
}

location @api_404 {
  return 404 '{"error": "API endpoint not found", "timestamp": "$time_iso8601"}';
  add_header Content-Type application/json;
}
      `.trim(),

      // Apache configuration
      apache: `
# .htaccess for React Router
<IfModule mod_rewrite.c>
  RewriteEngine On
  RewriteBase /
  RewriteRule ^index\\.html$ - [L]
  RewriteCond %{REQUEST_FILENAME} !-f
  RewriteCond %{REQUEST_FILENAME} !-d
  RewriteRule . /index.html [L]
</IfModule>

# Custom 404 error page
ErrorDocument 404 /404.html
      `.trim(),

      // Vercel configuration
      vercel: {
        "rewrites": [
          {
            "source": "/((?!api/).*)",
            "destination": "/index.html"
          }
        ],
        "routes": [
          {
            "src": "/api/(.*)",
            "status": 404,
            "headers": {
              "Content-Type": "application/json"
            },
            "dest": "/api/404.json"
          }
        ]
      },

      // Netlify configuration
      netlify: `
# _redirects file
/*    /index.html   200
/api/*  /api/404.json  404
      `.trim()
    };
  }

  /**
   * Export validation results
   */
  async exportResults(results, format = 'json') {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `route-validation-${timestamp}`;

    switch (format.toLowerCase()) {
      case 'json':
        return {
          filename: `${filename}.json`,
          content: JSON.stringify(results, null, 2),
          mimeType: 'application/json'
        };

      case 'csv':
        return {
          filename: `${filename}.csv`,
          content: this.convertToCSV(results),
          mimeType: 'text/csv'
        };

      case 'html':
        return {
          filename: `${filename}.html`,
          content: this.generateHTMLReport(results),
          mimeType: 'text/html'
        };

      default:
        throw new Error(`Unsupported format: ${format}`);
    }
  }

  /**
   * Convert results to CSV format
   */
  convertToCSV(results) {
    const rows = ['Environment,Route,Status,Response Time,Error'];
    
    Object.entries(results.environments).forEach(([envName, envResults]) => {
      envResults.routes.forEach(route => {
        rows.push([
          envName,
          route.path,
          route.isValid ? 'Valid' : 'Invalid',
          route.responseTime || 'N/A',
          route.error || ''
        ].map(field => `"${field}"`).join(','));
      });
    });

    return rows.join('\n');
  }

  /**
   * Generate HTML report
   */
  generateHTMLReport(results) {
    return `
<!DOCTYPE html>
<html>
<head>
  <title>Route Validation Report</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    .summary { background: #f5f5f5; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
    .environment { margin-bottom: 30px; }
    .environment h3 { color: #333; border-bottom: 2px solid #007bff; padding-bottom: 5px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #f2f2f2; }
    .valid { color: green; font-weight: bold; }
    .invalid { color: red; font-weight: bold; }
    .warning { color: orange; font-weight: bold; }
  </style>
</head>
<body>
  <h1>Route Validation Report</h1>
  <div class="summary">
    <h2>Summary</h2>
    <p><strong>Generated:</strong> ${results.timestamp}</p>
    <p><strong>Total Routes:</strong> ${results.summary.totalRoutes}</p>
    <p><strong>Valid Across All:</strong> <span class="valid">${results.summary.validAcrossAll}</span></p>
    <p><strong>Partially Valid:</strong> <span class="warning">${results.summary.partiallyValid}</span></p>
    <p><strong>Completely Invalid:</strong> <span class="invalid">${results.summary.completelyInvalid}</span></p>
  </div>

  ${Object.entries(results.environments).map(([envName, envResults]) => `
    <div class="environment">
      <h3>${envName.toUpperCase()} Environment</h3>
      <p><strong>Base URL:</strong> ${envResults.baseUrl}</p>
      <table>
        <thead>
          <tr>
            <th>Route</th>
            <th>Status</th>
            <th>Response Time</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          ${envResults.routes.map(route => `
            <tr>
              <td>${route.path}</td>
              <td class="${route.isValid ? 'valid' : 'invalid'}">${route.isValid ? 'Valid' : 'Invalid'}</td>
              <td>${route.responseTime ? route.responseTime + 'ms' : 'N/A'}</td>
              <td>${route.error || ''}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `).join('')}
</body>
</html>
    `.trim();
  }
}

/**
 * Convenience function for quick route validation
 */
export async function validateRoutes(environments = null) {
  const validator = new RouteValidatorManager();
  
  if (environments) {
    validator.environments = { ...validator.environments, ...environments };
  }
  
  return await validator.validateAllRoutes();
}

/**
 * Validate single route
 */
export async function validateSingleRoute(url) {
  const validator = new RouteValidatorManager();
  return await validator.validateSingleRoute(url);
}

export default RouteValidatorManager;
function validateAllRoutes(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: validateAllRoutes is not implemented yet.', args);
  return null;
}

export { validateAllRoutes };
function isRouteValid(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: isRouteValid is not implemented yet.', args);
  return null;
}

export { isRouteValid };
function getRouteSuggestions(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getRouteSuggestions is not implemented yet.', args);
  return null;
}

export { getRouteSuggestions };